import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { V1Thread } from '@/generated/hooks/zen/models';
import { ThreadSelector, type IThreadOption } from '@/shared/components';
import { formatActivityTimestamp, parseResourceName } from '@/shared/utils';

interface IQuillThreadSelectorProps {
  /** Threads available for the current automation. */
  threads: V1Thread[];
  /** Currently selected thread ID. */
  selectedThreadId?: string;
  /** Whether thread data is loading. */
  isLoading?: boolean;
  /** Whether a new thread is being created. */
  isCreating?: boolean;
  /** Called when the user selects an existing thread. */
  onSelectThread: (threadId: string) => void;
  /** Called when the user creates a new thread. */
  onCreateThread: () => void;
  /** Called when the user renames the selected thread. */
  onRenameThread: (displayName: string) => Promise<void>;
}

/**
 * Builds the base label for a chat thread, before any duplicate-suffix
 * disambiguation is applied. Untitled threads fall back to the default
 * ("New chat"); their timestamp is shown separately as the row description.
 *
 * @param thread - Thread resource from Zen
 * @param defaultLabel - Translated default label for untitled threads
 * @returns Thread label
 */
function getBaseThreadLabel(thread: V1Thread, defaultLabel: string): string {
  return thread.display_name?.trim() || defaultLabel;
}

/**
 * Builds dropdown options with duplicate base labels disambiguated by a
 * "(2)", "(3)" suffix in creation order. Keeps unique labels untouched.
 *
 * Untitled threads share the default "New chat" label and are otherwise
 * indistinguishable in the dropdown — the suffix lets users pick the right one.
 *
 * @param threads - Threads in the order returned by the API
 * @param defaultLabel - Translated default label for untitled threads.
 * @returns Dropdown options for valid threads (invalid resource names
 *   are dropped before label counting so they can't inflate the
 *   duplicate-suffix totals).
 */
function buildThreadOptions(
  threads: V1Thread[],
  defaultLabel: string,
): IThreadOption[] {
  const validEntries = threads
    .map(thread => ({
      thread,
      threadId: parseResourceName(thread.name).thread,
    }))
    .filter((entry): entry is { thread: V1Thread; threadId: string } =>
      Boolean(entry.threadId),
    );

  // Number duplicates in creation order so the oldest thread keeps the bare
  // label and newer ones get "(2)", "(3)" — independent of the display order.
  const suffixByThreadId = new Map<string, number>();
  const seenByLabel = new Map<string, number>();
  [...validEntries]
    .sort((a, b) =>
      (a.thread.create_time ?? '').localeCompare(b.thread.create_time ?? ''),
    )
    .forEach(entry => {
      const baseLabel = getBaseThreadLabel(entry.thread, defaultLabel);
      const seen = (seenByLabel.get(baseLabel) ?? 0) + 1;
      seenByLabel.set(baseLabel, seen);
      suffixByThreadId.set(entry.threadId, seen);
    });

  return validEntries.map(entry => {
    const baseLabel = getBaseThreadLabel(entry.thread, defaultLabel);
    const seen = suffixByThreadId.get(entry.threadId) ?? 1;
    const label = seen === 1 ? baseLabel : `${baseLabel} (${seen})`;
    return {
      value: entry.threadId,
      label,
      description: formatActivityTimestamp(entry.thread.create_time),
    };
  });
}

/**
 * Compact thread switcher for Quill automation chat.
 *
 * @param props - Selector props
 * @returns Thread selector header
 */
export function QuillThreadSelector({
  threads,
  selectedThreadId,
  isLoading = false,
  isCreating = false,
  onSelectThread,
  onCreateThread,
  onRenameThread,
}: IQuillThreadSelectorProps) {
  const { t } = useTranslation('automation');

  const options = useMemo<IThreadOption[]>(
    () =>
      buildThreadOptions(
        threads,
        t('automation.quillChat.thread.defaultTitle'),
      ),
    [threads, t],
  );

  if (isLoading && options.length === 0) {
    return null;
  }

  return (
    <ThreadSelector
      options={options}
      selectedThreadId={selectedThreadId}
      onSelectThread={onSelectThread}
      onCreateThread={onCreateThread}
      onRenameThread={onRenameThread}
      isCreating={isCreating}
      dataTestId="quill-thread-selector"
    />
  );
}
